import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TopNav } from "@/components/Layout";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/lib/settings";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Sparkles, Upload, Brain, Volume2, MessageCircle, ArrowRight, Zap, Building2, CheckCircle2,
  Activity, Clock, Palette, Bell, Bot, Headphones, ChevronDown, Play, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { LabPlansSection, PatientPricingSection, TaxFooter } from "@/components/pricing/PricingSections";

const features = [
  { icon: Sparkles, title: "Smart Report Engine", desc: "Convert any PDF or image lab report into a fully interactive Smart Report — with AI summaries, biomarker range bars, critical findings, and lifestyle guidance — in under 60 seconds.", bg: "bg-efgh-orange/10", fg: "text-efgh-orange" },
  { icon: Palette, title: "Complete White-label", desc: "Your logo, your colors, your domain. Patients visit reports.yourlabname.com — EFGH stays entirely invisible. Build your brand, not ours.", bg: "bg-efgh-yellow/10", fg: "text-efgh-yellow" },
  { icon: Activity, title: "Health Timeline & Trends", desc: "Every report auto-adds to a patient's personal health history. They compare biomarker trends across months — creating powerful loyalty to your lab.", bg: "bg-efgh-green/10", fg: "text-efgh-green" },
  { icon: Bell, title: "Follow-up Booking Engine", desc: "Configure intelligent rules: if glucose is above 100, auto-recommend HbA1c. Patient clicks 'Book Now' right inside their report — the booking lands back with you.", bg: "bg-efgh-blue/10", fg: "text-efgh-blue" },
  { icon: Bot, title: "Ask AI — Patient Education", desc: "Patients ask 'Why is my glucose high?' or 'What foods help my liver?' in English or Hindi. AI answers clearly and always reminds them to consult their doctor.", bg: "bg-efgh-orange/10", fg: "text-efgh-orange" },
  { icon: Headphones, title: "Audio Reports", desc: "Patients listen to their report instead of reading it. 2-minute quick summary or 5-minute detailed walkthrough. Male or female voice. English or Hindi.", bg: "bg-efgh-green/10", fg: "text-efgh-green" },
];

const steps = [
  { n: "1", title: "Lab generates PDF", desc: "Your LIS produces the report as usual. EFGH receives it automatically via integration, or your team uploads the PDF." },
  { n: "2", title: "AI extracts & analyses", desc: "Our OCR engine reads every parameter, value, unit, and reference range. AI flags critical results and writes patient-friendly explanations." },
  { n: "3", title: "Patient gets Smart Report", desc: "Patient receives an SMS/email with a link to their beautiful branded Smart Report — interactive, audio-ready, built for their phone." },
  { n: "4", title: "Revenue comes back to you", desc: "AI recommends follow-up tests. Patient books in one tap directly from the report — the appointment lands in your calendar. Automatic repeat business." },
];

const testimonials = [
  { by: "Dr. Suresh Kumar", role: "Owner & Pathologist, Shree Diagnostics, Pune", initials: "SK", quote: "Our patients started calling to say thank you — that never happened before. The Smart Report is the first thing they've ever actually understood. Follow-up bookings added ₹80,000 to our monthly revenue in just three months." },
  { by: "Rekha Patel", role: "Operations Head, HealthPath Labs, Ahmedabad", initials: "RP", quote: "We went live in 18 hours. The white-labeling is seamless — our logo, our colors, our domain. Patients genuinely think we built it. Our Google rating went from 3.8 to 4.6 in three months." },
  { by: "Arun Mehta", role: "CEO, Metro Path Labs, Delhi NCR", initials: "AM", quote: "The Ask AI feature is something else. Our elderly patients who couldn't understand their reports now feel informed and cared for. Patient retention went up 40%. ROI in month one." },
];

const faqs = [
  { q: "Does EFGH replace our Laboratory Information System (LIS)?", a: "No — EFGH is a patient-facing enhancement that sits on top of your existing LIS. Your LIS generates the PDF report as it always has. EFGH receives it, transforms it into an interactive Smart Report, and delivers it to your patient. Zero change to your internal lab workflow." },
  { q: "How long does it take to go live?", a: "Most labs are fully live within 24 hours of signing up. We handle branding setup (your logo, colors, domain), connect to your PDF delivery method, run a test batch of real reports, and walk you through the admin dashboard." },
  { q: "Is the AI medically accurate? Can patients rely on it?", a: "EFGH AI is trained on verified medical literature and Indian clinical guidelines (ICMR, NMC). It provides educational context only — every AI response includes a prominent reminder to consult a qualified physician before taking any health action." },
  { q: "Who sees patient data? Is it compliant with Indian data laws?", a: "Patient data is encrypted end-to-end using AES-256. EFGH is compliant with India's DPDPA 2023 and follows HIPAA-equivalent standards. We never sell, share, or use patient data for advertising. Data is stored on ISO 27001-certified infrastructure within India." },
  { q: "Can patients upload reports from other labs?", a: "Yes — via the patient self-service portal. Patients can upload any PDF or image lab report and get a Smart Report generated instantly for ₹5–10 per conversion." },
  { q: "What languages does EFGH support?", a: "Version 1 fully supports English and Hindi — including UI, AI responses, and audio reports. Tamil, Telugu, Bengali, Marathi, and Gujarati are on our roadmap." },
];

export default function MarketingHome() {
  const nav = useNavigate();
  const { settings } = useSiteSettings();
  const [cms, setCms] = useState(null);
  useEffect(() => { api.get("/marketing/content").then((r) => setCms(r.data)).catch(() => {}); }, []);

  const hero = cms?.hero;
  const stats = cms?.stats;
  const testimonialsList = cms?.testimonials || testimonials;
  const faqsList = cms?.faqs || faqs;
  const bookLabel = cms?.hero_book_label || settings?.hero_book_label || "Book a Test";
  const bookUrl = cms?.hero_book_url || settings?.hero_book_url || "/book-tests";
  const ctaPrimaryUrl = cms?.hero_cta_primary_url || settings?.hero_cta_primary_url || "#contact";
  const ctaSecondaryUrl = cms?.hero_cta_secondary_url || settings?.hero_cta_secondary_url || "#demo";
  const isExt = (u) => /^https?:\/\//i.test(u || "");
  const logo = cms?.logo_url || settings?.logo_url || "/efgh-logo.png";
  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      {/* ============ HERO ============ */}
      <section id="hero" className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 -left-24 w-96 h-96 rounded-full blur-3xl opacity-25" style={{ background: "radial-gradient(circle, #E55A25, transparent 70%)" }} />
          <div className="absolute top-40 right-0 w-96 h-96 rounded-full blur-3xl opacity-25" style={{ background: "radial-gradient(circle, #4CAF50, transparent 70%)" }} />
          <div className="absolute bottom-0 left-1/3 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: "radial-gradient(circle, #5A5AC7, transparent 70%)" }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <span data-testid="hero-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-heading font-semibold uppercase tracking-widest text-ink bg-gradient-to-r from-efgh-orange/20 via-efgh-yellow/20 to-efgh-green/20">
              <Zap className="w-3.5 h-3.5" /> {hero?.badge || "Now live for Indian laboratories"}
            </span>
            <h1 className="mt-5 font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
              {hero?.title_l1 || "Your lab report,"}<br /><span className="italic font-normal" style={{ color: "#E55A25" }}>{hero?.title_l2 || "finally understood"}</span>
            </h1>
            <p className="mt-5 text-lg text-ink/70 max-w-xl">
              {hero?.subtitle || "EFGH converts traditional diagnostic PDFs into AI-powered Smart Reports — interactive, patient-friendly, and branded for your laboratory."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {isExt(bookUrl) ? (
                <a href={bookUrl} target="_blank" rel="noreferrer"><Button data-testid="hero-book-test" className="h-12 px-6 bg-efgh-orange hover:bg-efgh-orange/90 text-white text-base shadow-lg shadow-efgh-orange/25"><Calendar className="w-4 h-4 mr-2" /> {bookLabel}</Button></a>
              ) : (
                <Link to={bookUrl}><Button data-testid="hero-book-test" className="h-12 px-6 bg-efgh-orange hover:bg-efgh-orange/90 text-white text-base shadow-lg shadow-efgh-orange/25"><Calendar className="w-4 h-4 mr-2" /> {bookLabel}</Button></Link>
              )}
              {isExt(ctaPrimaryUrl) ? (
                <a href={ctaPrimaryUrl} target="_blank" rel="noreferrer"><Button data-testid="hero-cta" variant="outline" className="h-12 px-6 border-ink text-ink hover:bg-muted text-base">{hero?.cta_primary || "For Labs: Start Free Trial"} <ArrowRight className="w-4 h-4 ml-2" /></Button></a>
              ) : (
                <a href={ctaPrimaryUrl}><Button data-testid="hero-cta" variant="outline" className="h-12 px-6 border-ink text-ink hover:bg-muted text-base">{hero?.cta_primary || "For Labs: Start Free Trial"} <ArrowRight className="w-4 h-4 ml-2" /></Button></a>
              )}
              <a href={ctaSecondaryUrl} target={isExt(ctaSecondaryUrl) ? "_blank" : undefined} rel={isExt(ctaSecondaryUrl) ? "noreferrer" : undefined}><Button variant="ghost" className="h-12 px-4 text-ink/70 hover:text-ink"><Play className="w-4 h-4 mr-2" /> {hero?.cta_secondary || "90-sec Demo"}</Button></a>
            </div>
            <div className="mt-3 text-xs text-ink/50">
              🩺 Book at NABL-accredited labs across India · 500+ tests · Home collection · Smart AI reports
            </div>
            <div className="mt-8 flex items-center gap-3 text-sm text-ink/60">
              <div className="flex -space-x-2">
                {["SL", "DL", "MP", "HH", "RK"].map((s, i) => (
                  <div key={s} className="w-8 h-8 rounded-full border-2 border-white grid place-items-center text-[10px] font-heading font-bold text-white" style={{ background: ["#E55A25", "#F5C400", "#4CAF50", "#5A5AC7", "#0B1628"][i] }}>{s}</div>
                ))}
              </div>
              Trusted by <b className="text-ink">200+ laboratories</b> across India
            </div>
          </div>
          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "150ms" }}>
            <HeroPreview />
          </div>
        </div>
      </section>

      {/* ============ STATS BAND ============ */}
      <section className="border-y border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {(stats || [
            { k: "200+", v: "Labs onboarded" }, { k: "8L+", v: "Smart Reports generated" },
            { k: "4.8/5", v: "Patient satisfaction" }, { k: "3×", v: "Repeat booking lift" },
          ]).map((s) => (
            <div key={s.v}>
              <div className="font-heading font-bold text-3xl text-ink">{s.k}</div>
              <div className="text-xs uppercase tracking-widest text-ink/50 mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section id="for-labs" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold">For Diagnostic Laboratories</div>
          <h2 className="mt-2 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-ink max-w-3xl">{cms?.features_heading || "The patient experience your lab deserves"}</h2>
          <p className="mt-4 text-ink/70 max-w-2xl">{cms?.features_subheading || "EFGH plugs into your existing workflow. No LIS change. No disruption."}</p>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <Card key={f.title} className="p-6 rounded-2xl card-hover" data-testid={`feature-${f.title.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className={`w-12 h-12 rounded-xl grid place-items-center ${f.bg} ${f.fg}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="mt-4 font-heading font-semibold text-lg text-ink">{f.title}</div>
                  <p className="mt-2 text-sm text-ink/65">{f.desc}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how" className="py-20 lg:py-28 bg-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/50 font-heading font-semibold">How EFGH works</div>
          <h2 className="mt-2 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl max-w-3xl">From PDF to Smart Report <span className="italic font-normal" style={{ color: "#F5C400" }}>in under 60 seconds</span></h2>
          <p className="mt-4 text-white/60 max-w-2xl">Zero change to your existing workflow. EFGH runs silently in the background.</p>
          <div className="mt-12 grid md:grid-cols-4 gap-4">
            {steps.map((s, i) => (
              <div key={s.n} className="relative">
                <div className="w-10 h-10 rounded-lg grid place-items-center font-heading font-bold" style={{ background: ["#E55A25", "#F5C400", "#4CAF50", "#5A5AC7"][i], color: i === 1 ? "#0B1628" : "#fff" }}>{s.n}</div>
                <div className="mt-4 font-heading font-semibold text-lg">{s.title}</div>
                <p className="mt-2 text-sm text-white/60">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold">What labs are saying</div>
          <h2 className="mt-2 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-ink max-w-3xl">Real results from <span className="italic font-normal" style={{ color: "#5A5AC7" }}>real laboratories</span></h2>
          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {testimonialsList.map((t) => (
              <Card key={t.by} className="p-6 rounded-2xl card-hover">
                <div className="text-efgh-yellow text-xl">★★★★★</div>
                <p className="mt-3 text-ink/80 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-ink text-white grid place-items-center text-xs font-heading font-bold">{t.initials}</div>
                  <div>
                    <div className="font-medium text-ink text-sm">{t.by}</div>
                    <div className="text-xs text-ink/50">{t.role}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      {/* Pricing across the entire site is driven exclusively by Super Admin's Pricing Management
          module (persisted in `platform_settings._id == "pricing"`, exposed via
          `GET /api/public/platform-settings`). Editing prices there updates Home, /pricing,
          /for-labs, and the wallet dialogs simultaneously. */}
      <section id="pricing" className="py-16 lg:py-20 bg-muted/30 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold text-center">Pricing</div>
          <h2 className="mt-2 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-ink text-center">{cms?.pricing_heading || "Simple, transparent pricing"}</h2>
          <p className="mt-4 text-ink/70 text-center max-w-2xl mx-auto">{cms?.pricing_subheading || "Labs subscribe. Patients top up as they go. All prices managed live from a single dashboard."}</p>
        </div>

        <LabPlansSection
          eyebrow="For laboratories"
          heading="Plans for diagnostic labs"
          ctaTo="/for-labs"
          ctaLabel="Start free trial"
          freeLabel="Start free"
          className="!py-10"
        />
        <PatientPricingSection
          eyebrow="For patients & individuals"
          heading="Pay-per-report for patients"
          ctaTo="/register?role=patient"
          className="!py-4 pt-0"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <TaxFooter className="mt-6" />
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section id="faq" className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold">FAQ</div>
          <h2 className="mt-2 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-ink">Questions labs ask us</h2>
          <div className="mt-8 space-y-2">
            {faqsList.map((f, i) => <Faq key={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* ============ CONTACT ============ */}
      <section id="contact" className="py-20 lg:py-28 bg-ink text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/50 font-heading font-semibold">Get started</div>
            <h2 className="mt-2 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl">{cms?.contact_heading || "Start your free trial today"}</h2>
            <p className="mt-4 text-white/70">{cms?.contact_subheading || "Join 200+ laboratories already using EFGH."}</p>
            <ul className="mt-6 space-y-3 text-sm text-white/80">
              <li className="flex gap-3"><Zap className="w-5 h-5 text-efgh-yellow shrink-0" /><span><b>Live in under 24 hours</b> — Our onboarding team sets up branding, domain and integration.</span></li>
              <li className="flex gap-3"><Palette className="w-5 h-5 text-efgh-orange shrink-0" /><span><b>Fully white-labelled</b> — Patients see your brand, your domain, your colors.</span></li>
              <li className="flex gap-3"><Activity className="w-5 h-5 text-efgh-green shrink-0" /><span><b>ROI from month one</b> — Follow-up recommendations typically add ₹50k–₹1.5L in monthly revenue within 90 days.</span></li>
            </ul>
          </div>
          <LeadForm />
        </div>
      </section>

      <footer className="border-t border-border bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between flex-wrap gap-3 text-sm text-ink/50">
          <div className="flex items-center gap-3">
            <img src={logo} alt="EFGH" className="h-10" />
          </div>
          <div>{cms?.footer_copyright || "© 2026 EFGH Health Technologies"} · <a href={`mailto:${cms?.footer_email || "labs@efgh.ai"}`} className="hover:text-ink">{cms?.footer_email || "labs@efgh.ai"}</a></div>
        </div>
      </footer>
    </div>
  );
}

/* ============ HERO PREVIEW ============ */
function HeroPreview() {
  const { settings } = useSiteSettings();
  const logo = settings?.logo_url || "/efgh-logo.png";
  return (
    <div className="relative">
      <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-efgh-orange/20 via-efgh-yellow/20 to-efgh-green/20 blur-2xl" />
      <Card className="relative rounded-2xl overflow-hidden shadow-2xl">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <img src={logo} alt="EFGH" className="h-10" />
          <span className="text-[10px] uppercase tracking-widest text-ink/50">Smart Report</span>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-4">
            <div className="relative w-20 h-20">
              <svg viewBox="0 0 100 100" className="-rotate-90 w-full h-full">
                <circle cx="50" cy="50" r="42" strokeWidth="8" stroke="#EEF0F4" fill="none" />
                <circle cx="50" cy="50" r="42" strokeWidth="8" stroke="#4CAF50" fill="none" strokeLinecap="round" strokeDasharray="200 264" />
              </svg>
              <div className="absolute inset-0 grid place-items-center font-heading font-bold text-2xl text-ink">76</div>
            </div>
            <div>
              <div className="font-heading font-semibold text-ink">Good Health</div>
              <div className="text-xs text-ink/50 mt-0.5">28 tests · 4 concerns</div>
            </div>
          </div>
          <div className="mt-5 space-y-2">
            {[
              { n: "Blood Glucose", v: "112", s: "HIGH", c: "text-danger", b: "bg-danger-light" },
              { n: "Hemoglobin", v: "14.9", s: "OK", c: "text-success", b: "bg-success-light" },
              { n: "SGPT (Liver)", v: "45.1", s: "WATCH", c: "text-warning", b: "bg-warning-light" },
            ].map((r) => (
              <div key={r.n} className={`flex items-center justify-between p-3 rounded-lg ${r.b}`}>
                <div className="text-sm font-medium text-ink">{r.n}</div>
                <div className="flex items-center gap-2"><div className="font-heading font-bold text-ink">{r.v}</div><span className={`text-[10px] font-bold ${r.c}`}>{r.s}</span></div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 rounded-lg bg-gradient-to-br from-efgh-orange/10 via-efgh-yellow/10 to-efgh-green/10 text-xs text-ink/85">
            <b>🤖 AI:</b> Your glucose is slightly high. Try cutting sugary drinks and walking 30 mins after meals.
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ============ FAQ ITEM ============ */
function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between py-5 text-left" data-testid={`faq-toggle-${q.slice(0, 10)}`}>
        <div className="font-heading font-semibold text-ink pr-4">{q}</div>
        <ChevronDown className={`w-5 h-5 text-ink/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-5 text-sm text-ink/70 leading-relaxed">{a}</div>}
    </div>
  );
}

/* ============ LEAD FORM ============ */
function LeadForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", lab: "", city: "", volume: "", lis: "", notes: "" });
  const [sent, setSent] = useState(false);
  const submit = (e) => { e.preventDefault(); setSent(true); toast.success("We'll reach out within 2 hours."); };
  if (sent) {
    return (
      <Card className="p-8 rounded-2xl text-center bg-white text-ink">
        <div className="text-4xl">✅</div>
        <div className="mt-3 font-heading font-bold text-xl">You&apos;re on the list!</div>
        <p className="mt-2 text-ink/60 text-sm">We&apos;ll reach out to <b>{form.name}</b> within 2 hours to get your lab set up.</p>
      </Card>
    );
  }
  return (
    <Card className="p-6 rounded-2xl bg-white text-ink">
      <div className="font-heading font-bold text-xl">Request your free trial</div>
      <p className="text-sm text-ink/60">Fill in your details and we&apos;ll reach out within 2 hours.</p>
      <form onSubmit={submit} className="mt-4 grid grid-cols-2 gap-3">
        <div className="col-span-2 sm:col-span-1"><Label>Your Name *</Label><Input required data-testid="lead-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div className="col-span-2 sm:col-span-1"><Label>Phone Number *</Label><Input required data-testid="lead-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
        <div className="col-span-2"><Label>Email Address *</Label><Input required type="email" data-testid="lead-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div className="col-span-2 sm:col-span-1"><Label>Laboratory Name</Label><Input value={form.lab} onChange={(e) => setForm({ ...form, lab: e.target.value })} /></div>
        <div className="col-span-2 sm:col-span-1"><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
        <div className="col-span-2"><Label>Reports per month (approx.)</Label>
          <select value={form.volume} onChange={(e) => setForm({ ...form, volume: e.target.value })} className="w-full h-10 px-3 border border-input rounded-md text-sm">
            <option value="">Select range</option>
            <option>Under 500</option><option>500 – 2,000</option><option>2,000 – 10,000</option><option>10,000+</option>
          </select>
        </div>
        <div className="col-span-2"><Label>Current LIS / Software</Label><Input value={form.lis} onChange={(e) => setForm({ ...form, lis: e.target.value })} /></div>
        <div className="col-span-2"><Label>Anything specific you&apos;d like to discuss?</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        <Button type="submit" data-testid="lead-submit" className="col-span-2 h-11 bg-ink hover:bg-ink/90 text-white">Request Free Trial →</Button>
      </form>
      <p className="mt-3 text-xs text-ink/40 text-center">🔒 Your details are encrypted and never shared. No spam, ever.</p>
    </Card>
  );
}
